import { cn } from '@src/design-system/helpers';

import { type ComponentProps } from 'react';
import { EditableCell } from './editable-cell';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { ArrowSquareDown } from 'iconsax-react';
import { RadioIndicator } from '@src/design-system/radio';
import { Checkbox } from '@src/design-system/checkbox';
import {
  ShowSpecificNumberContext,
  ShowSpecificNumberExtra,
  ShowSpecificNumberItem,
} from '@src/design-system/show-specific-number';

export function EditableDropdownCell({ ...props }: ComponentProps<'div'>) {
  return (
    <DropdownMenu>
      <EditableCell {...props} />
    </DropdownMenu>
  );
}

export function EditableDropdownCellEditMode({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenuContent
      align='start'
      style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
      className={cn(
        'max-h-[300px] max-w-auto w-auto  overflow-y-auto',
        className
      )}
      {...props}
    />
  );
}

export function EditableDropdownCellItem({
  children,
  className,
  isSelected,
  type = 'radio',
  ...props
}: ComponentProps<typeof DropdownMenuItem> & {
  isSelected?: boolean;
  type?: 'radio' | 'checkbox';
}) {
  return (
    <DropdownMenuItem
      className={cn(
        'flex items-center gap-2 min-w-0',
        isSelected && 'bg-primary-50 text-primary-1000',
        className
      )}
      {...props}
    >
      {type === 'radio' ? (
        <RadioIndicator checked={isSelected} />
      ) : (
        <Checkbox checked={isSelected} />
      )}

      <span
        className='truncate min-w-0'
        ref={(node) => {
          if (!node) return;
          node.title = node?.textContent || '';
        }}
      >
        {children}
      </span>
    </DropdownMenuItem>
  );
}

export const EditableDropdownCellViewModeItem = ({
  className,
  ...props
}: ComponentProps<typeof ShowSpecificNumberItem>) => (
  <ShowSpecificNumberItem
    className={cn('truncate w-full min-w-0', className)}
    {...props}
  />
);

EditableDropdownCellViewModeItem.displayName =
  ShowSpecificNumberItem.displayName;

export function EditableDropdownCellViewModeExtra({
  className,
  ...props
}: ComponentProps<typeof ShowSpecificNumberExtra>) {
  return (
    <ShowSpecificNumberExtra
      className={cn(
        'text-primary-1000 bg-primary-100 text-xs font-medium rounded-md shrink-0 me-2 size-6',
        className
      )}
      {...props}
    />
  );
}

function EditableDropdownCellViewModeContent({
  className,
  ...props
}: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex-1 flex items-center gap-2 min-w-0 truncate',
        className
      )}
      {...props}
    />
  );
}

export function EditableDropdownCellViewMode({
  className,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuTrigger>) {
  return (
    <DropdownMenuTrigger
      className={cn(
        'group flex absolute inset-0 p-2 items-center justify-between text-start w-full h-full cursor-pointer',
        className
      )}
      {...props}
    >
      <ShowSpecificNumberContext maxItems={1}>
        <EditableDropdownCellViewModeContent className='flex items-center gap-2 min-w-0 truncate'>
          <span className='truncate'>{children}</span>
          <EditableDropdownCellViewModeExtra />
        </EditableDropdownCellViewModeContent>
      </ShowSpecificNumberContext>

      <ArrowSquareDown
        size={16}
        className='text-primary-1000 shrink-0 group-hover:block hidden'
      />
    </DropdownMenuTrigger>
  );
}
